import React, { useState, ChangeEvent, useEffect } from "react";


import { useNavigate } from "react-router-dom";

import { AuthenticationForm } from "@components/ui";
import { Field as FormField } from "@components/ui";

import { ComposeURLWithGetQueryArguments } from "@shared/utils";
import { PagePaths } from "@shared/constants";
import { TimeForNewVerify } from "@components/ui";
import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import { t_insertUsernameCode } from "@i18n/usualTexts/t_insertUsernameCode";
import {
  cleanFormDataErrors,
} from "@shared/utils";
import { CacheService, PASSWORD_POST_RESPONSE, SuccessResponse, OpenAPI } from "@shared/services";
import { delay, intervalMiliseconds } from "@shared/utils";
import { SendConfirmationCodePost } from "@features/createaccount/service/AccountRequest";
import { useAuthError } from "@shared/hooks/useAuthError";
import { setState } from "@shared/utils";
import { useOAuth2, OAuth2Type } from "@shared/contexts";
import { AuthType, useAuthentication } from "@shared/contexts";
import { useRecaptchaContext } from "@shared/contexts/RecaptchaContext";
import AccountService from "@features/createaccount/service/AccountService";
import { VerifyEmailPost } from "@features/authentication/services/OAuth2Request";
import { isSuccessTokenStepResponse } from "@shared/utils/typeGuards";
import { JwtDecoder } from "@shared/utils/jwt/JwtDecoder";


interface FormData {
  usernamecode: {
    value: string;
    errors: Set<string>;
  };
}

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useDocumentTitle(t("login.identity.pageTitle"));
  const { handleOAuth2Error } = useAuthError();
  const [submitted, setSubmitted] = useState(false);
  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { oauth2Data, state, startAuthenticationRequestResponse } = useOAuth2();
  const { authData, setAuthData} = useAuthentication();
  const { currentToken: antiBotToken } = useRecaptchaContext();
  const [formData, setFormData] = useState<FormData>({
    usernamecode: {
      value: "",
      errors: new Set<string>(),
    },
  });

  const [emailNotVerifiedEmail, setEmailNotVerifiedEmail] = useState<string | null>(null);

  useEffect(() => {
    try {
      const storedEmail = sessionStorage.getItem('emailNotVerifiedEmail');
      if (storedEmail) {
        setEmailNotVerifiedEmail(storedEmail);
        sessionStorage.removeItem('emailNotVerifiedEmail');
      }
    } catch {
      // Ignore errors
    }
  }, []);

  // Helper function to check if sessionStorage indicates social login
  const checkSessionStorageForSocialLogin = (): { isSocialLogin: boolean; storedToken: any } => {
    try {
      const isSocialLogin = sessionStorage.getItem('isSocialLogin') === 'true';
      const storedTokenStr = sessionStorage.getItem('socialLoginToken');
      const storedToken = storedTokenStr ? JSON.parse(storedTokenStr) : null;
      return { isSocialLogin, storedToken };
    } catch (e) {
      return { isSocialLogin: false, storedToken: null };
    }
  };

  const checkTokenClaimsForSocialLogin = (token: string): boolean => {
    try {
      const claims = JwtDecoder.getAllClaims(token);
      if (!claims) return false;
      return hasSocialLoginProvider(claims);
    } catch (e) {
      console.warn('Failed to check token claims for social login:', e);
      return false;
    }
  };

  const SOCIAL_LOGIN_PROVIDERS = ['google', 'facebook'] as const;

  function hasSocialLoginProvider(claims: any): boolean {
    return SOCIAL_LOGIN_PROVIDERS.some(provider =>
      claims.additional_claims?.provider === provider || claims.iss?.includes(provider)
    );
  }

  // Helper function to get token to use
  const getTokenToUse = (storedToken: any): any => {
    if (storedToken) {
      return storedToken;
    }
    if (startAuthenticationRequestResponse && 
        isSuccessTokenStepResponse(startAuthenticationRequestResponse)) {
      return startAuthenticationRequestResponse;
    }
    return null;
  };

  // Helper function to clear sessionStorage social login flags
  const clearSocialLoginSessionFlags = (): void => {
    try {
      sessionStorage.removeItem('isSocialLogin');
      sessionStorage.removeItem('socialLoginProvider');
    } catch (e) {
      // Ignore errors
    }
  };

  const detectSocialLoginFromToken = (): boolean => {
    if (!startAuthenticationRequestResponse || 
        !isSuccessTokenStepResponse(startAuthenticationRequestResponse) ||
        !startAuthenticationRequestResponse.access_token) {
      return false;
    }
    return checkTokenClaimsForSocialLogin(startAuthenticationRequestResponse.access_token);
  };

  // Initialize social login token if coming from social login
  useEffect(() => {
    const { isSocialLogin: isSocialFromStorage, storedToken } = checkSessionStorageForSocialLogin();
    const isSocialLogin = isSocialFromStorage || detectSocialLoginFromToken();
    const tokenToUse = getTokenToUse(storedToken);
    
    if (isSocialLogin && tokenToUse && !authData.socialLoginTokenResponse) {
      setAuthData({
        ...authData,
        socialLoginTokenResponse: tokenToUse,
        isSocialLogin: true,
        step: 3,
      });
      clearSocialLoginSessionFlags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAuthenticationRequestResponse, authData, setAuthData]);

  // Helper function to check if token is expired
  const isTokenExpired = (expirationTime: number, bufferTime: number = 30): boolean => {
    const currentTime = Math.floor(Date.now() / 1000);
    return expirationTime <= (currentTime + bufferTime);
  };

  // Helper function to get social login token response
  const getSocialLoginTokenResponse = (): any => {
    if (authData.socialLoginTokenResponse) {
      return authData.socialLoginTokenResponse;
    }
    if (startAuthenticationRequestResponse && 
        isSuccessTokenStepResponse(startAuthenticationRequestResponse)) {
      return startAuthenticationRequestResponse;
    }
    return null;
  };

  // Helper function to validate social login token
  const validateSocialLoginToken = (): boolean => {
    const tokenResponse = getSocialLoginTokenResponse();
    
    if (!tokenResponse) {
      console.warn("Missing social login token, redirecting to identity step");
      setAuthData({ ...authData, step: 1 });
      navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.IDENTITY));
      return false;
    }

    if (isTokenExpired(tokenResponse.expiration_time)) {
      console.warn("Social login token expired, redirecting to identity step");
      setAuthData({
        ...authData,
        step: 1,
        socialLoginTokenResponse: undefined,
        isSocialLogin: false,
      });
      navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.IDENTITY));
      return false;
    }

    return true;
  };

  // Helper function to validate regular login token
  const validateRegularLoginToken = (): boolean => {
    const passwordResponse = authData.passwordRequestResponse;
    if (!passwordResponse) {
      console.warn("Missing password data, redirecting to password step");
      setAuthData({ ...authData, step: 2 });
      navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.PASSWORD));
      return false;
    }

    if (isTokenExpired(passwordResponse.expiration_time)) {
      console.warn("Password token expired, redirecting to password step");
      setAuthData({
        ...authData,
        step: 2,
        passwordRequestResponse: undefined,
      });
      navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.PASSWORD));
      return false;
    }

    return true;
  };

  // Helper function to check if user is coming from social login
  const checkIsSocialLogin = (): boolean => {
    if (authData.isSocialLogin) {
      return true;
    }
    try {
      return sessionStorage.getItem('isSocialLogin') === 'true';
    } catch (e) {
      return false;
    }
  };

  // Validate token consistency when page loads
  useEffect(() => {
    try {
      const hasPendingToken = sessionStorage.getItem('socialLoginToken');
      const isUninitializedState = !authData.socialLoginTokenResponse && !authData.passwordRequestResponse;
      if (hasPendingToken && isUninitializedState) return;
    } catch {}

    const isSocialLogin = checkIsSocialLogin();
    
    if (isSocialLogin) {
      validateSocialLoginToken();
    } else {
      validateRegularLoginToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData, setAuthData, navigate, startAuthenticationRequestResponse]);

  const getStoredSocialLoginToken = (): any | null => {
    try {
      const storedTokenStr = sessionStorage.getItem('socialLoginToken');
      return storedTokenStr ? JSON.parse(storedTokenStr) : null;
    } catch (e) {
      return null;
    }
  };

  const getStartAuthToken = (): any | null => {
    if (startAuthenticationRequestResponse && 
        isSuccessTokenStepResponse(startAuthenticationRequestResponse)) {
      return startAuthenticationRequestResponse;
    }
    return null;
  };

  const getSocialLoginToken = (): string => {
    const tokenResponse = authData.socialLoginTokenResponse 
      || getStoredSocialLoginToken() 
      || getStartAuthToken();
    
    const token = tokenResponse?.access_token;
    if (!token) {
      throw new Error("No valid social login token found");
    }
    return token;
  };

  // Helper function to get password token
  const getPasswordToken = (): string => {
    const token = authData.passwordRequestResponse?.access_token;
    if (!token) {
      throw new Error("No valid password token found");
    }
    return token;
  };

  // Helper function to get token based on login type
  const getTokenForVerification = (): string => {
    if (authData.isSocialLogin) {
      return getSocialLoginToken();
    }
    return getPasswordToken();
  };

  // Helper function to clear social login token from sessionStorage
  const clearSocialLoginTokenFromStorage = (): void => {
    if (authData.isSocialLogin) {
      try {
        sessionStorage.removeItem('socialLoginToken');
      } catch (e) {
        // Ignore errors
      }
    }
  };

  function getTokenResponseForDelays(): any {
    if (authData.isSocialLogin) {
      return authData.socialLoginTokenResponse || getStartAuthToken();
    }
    return authData.passwordRequestResponse;
  }

  function isInDelayRange(ms: number, maxMs: number): boolean {
    return ms > 0 && ms < maxMs;
  }

  async function applyDelaysIfNeeded(): Promise<void> {
    const tokenResponse = getTokenResponseForDelays();
    if (!tokenResponse) return;

    const ms1 = intervalMiliseconds(tokenResponse);
    if (isInDelayRange(ms1, 5000)) await delay(ms1);

    const ms2 = intervalMiliseconds(tokenResponse);
    if (isInDelayRange(ms2, 2000)) await delay(ms2);
  }

  async function post_and_navigate(newAuthData: AuthType) {
    const token = getTokenForVerification();
    
    await applyDelaysIfNeeded();
    const verifyEmailResponse = await VerifyEmailPost(formData.usernamecode.value, oauth2Data, token, antiBotToken || undefined);
    
    clearSocialLoginTokenFromStorage();
    
    console.log(verifyEmailResponse);
    setState(state);
    
    // After email verification returns SuccessResponse, redirect to /auth/user/authenticate
    // to complete the OAuth2 flow and get the authorization code
    await handleSuccessResponseRedirect(verifyEmailResponse, token);
  }

  function buildOAuth2RedirectParams(
    token: string,
    oauth2Data: OAuth2Type,
    state: string | undefined
  ): URLSearchParams {
    const params = new URLSearchParams({
      code: token,
      response_type: oauth2Data.response_type || 'code',
      client_id: oauth2Data.client_id,
      redirect_uri: oauth2Data.redirect_uri,
    });

    const optionalParams: Array<[string, string | undefined]> = [
      ['scope', oauth2Data.scope],
      ['state', state],
      ['code_challenge', oauth2Data.code_challenge],
      ['code_challenge_method', oauth2Data.code_challenge_method],
    ];
    optionalParams.forEach(([key, value]) => { if (value) params.append(key, value); });

    return params;
  }

  async function handleSuccessResponseRedirect(successResponse: SuccessResponse, token: string): Promise<void> {
    try {
      const baseUrl = OpenAPI?.BASE || window.location.origin;
      const params = buildOAuth2RedirectParams(token, oauth2Data, state);
      const authenticateUrl = `${baseUrl}/auth/user/authenticate?${params.toString()}`;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[VerifyEmail] Redirecting to authenticate endpoint:', authenticateUrl);
      }
      
      // Redirect to authenticate endpoint which will handle the final redirect
      window.location.href = authenticateUrl;
    } catch (error) {
      console.error('[VerifyEmail] Error constructing redirect URL:', error);
      // Fallback: redirect to client's redirect_uri directly
      window.location.href = oauth2Data.redirect_uri;
    }
  }

  function handleFormData(e: ChangeEvent<HTMLInputElement>) {
    const { value, name } = e.target;

    formData[name as keyof FormData].value = value;
    setFormData({
      ...formData,
    });
  }

  function back() {
    const backStep = authData.isSocialLogin ? 1 : 2;
    const backPage = authData.isSocialLogin ? PagePaths.LOGIN.IDENTITY : PagePaths.LOGIN.PASSWORD;
    setAuthData({ ...authData, step: backStep });
    navigate(ComposeURLWithGetQueryArguments(backPage));
  }

  async function handleSubmit() {
    setSubmitted(true);
    setIsLoading(true);

    const step = 6;

    const newAuthData: AuthType = {
      ...authData,
      step: step,
    };

    try {
      checkThisFormData()

      await post_and_navigate(newAuthData);
    } catch (e) {
      const error = JSON.parse(JSON.stringify(e));
      console.log(error)
      handleOAuth2Error("usernamecode", error, setServerErrorPopUp, formData.usernamecode.errors);
    } finally {
      setIsLoading(false);

      setFormData({
        ...formData,
      });

      await delay(1000);
      setSubmitted(false);
    }

    setFormData({
      ...formData,
    });
  }

  function getSocialLoginTokenForResend(): string | undefined {
    const tokenResponse = authData.socialLoginTokenResponse 
      || getStoredSocialLoginToken() 
      || getStartAuthToken();
    return tokenResponse?.access_token;
  }

  async function getTokenForResend(): Promise<string | undefined> {
    if (authData.isSocialLogin) {
      return getSocialLoginTokenForResend();
    }
    
    const cache = new CacheService(
      authData,
      PASSWORD_POST_RESPONSE,
      authData
    );
    const passwordPostResponse = cache.getToCache();
    return passwordPostResponse?.access_token;
  }

  function isUnauthorizedResendError(error: any): boolean {
    const responseStatus = error && error.response && error.response.status;
    const directStatus = error && error.status;
    return responseStatus === 401 || directStatus === 401;
  }

  function handleResendError(error: any) {
    if (isUnauthorizedResendError(error)) {
      window.location.reload();
    }
  }

  async function sendCodeEmail() {
    console.log("send code email");
    
    const token = await getTokenForResend();
    const resolvedAntiBotToken = antiBotToken || undefined;
    
    console.log(token);
    try {
      await SendConfirmationCodePost(token, resolvedAntiBotToken);
    } catch (error: any) {
      console.log(error);
      handleResendError(error);
    }
  }

  function checkThisFormData() {
    cleanFormDataErrors(formData);

    const accountService: AccountService = new AccountService(t);
    accountService.checkCode(formData.usernamecode.value, "usernamecode");
  }

  const subtitle = emailNotVerifiedEmail
    ? t(t_insertUsernameCode.subtitle) + ` (${emailNotVerifiedEmail})`
    : t(t_insertUsernameCode.subtitle);

  return (
    <AuthenticationForm
      title={t(t_insertUsernameCode.title)}
      subtitle={subtitle}
      back={back}
      onSubmit={() => handleSubmit()}
      isLoading={isLoading}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
    >
      <div>
        <FormField
          autofocus={true}
          name="usernamecode"
          type="text"
          required={true}
          onChange={handleFormData}
          value={formData.usernamecode.value}
          errors={formData.usernamecode.errors}
          submitted={submitted}
        >
          {t(t_insertUsernameCode.codePlaceholder)}
        </FormField>
      </div>

      <TimeForNewVerify sendCodeEmail={sendCodeEmail} />
    </AuthenticationForm>
  );
};

export default VerifyEmailPage;
